import { useState, useEffect, useRef } from 'react';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCE_STATS_QUERY, CADENCE_STEP_STATS_QUERY, CADENCE_GOAL_PROGRESS_QUERY } from '../utils/cadence-queries';

/**
 * Fetch stats, step stats, and goal progress for a cadence.
 * @param {string|number} cadenceId
 * @param {Object} [options]
 * @param {string} [options.stepStatsTimeRange] - PerformanceTimeRange for step stats (e.g. '7d', '30d', 'all')
 * Returns { stats, stepStats, goalProgress, loading, error, refetch }.
 */
const useCadenceStats = (cadenceId, options = {}) => {
  const [stats, setStats] = useState(null);
  const [stepStats, setStepStats] = useState([]);
  const [goalProgress, setGoalProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!cadenceId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Only show full loading state on initial load — keep stale data during refetch
    if (!hasLoadedRef.current) {
      setLoading(true);
      setStats(null);
      setStepStats([]);
      setGoalProgress(null);
    }
    setError(null);

    const id = String(cadenceId);

    Promise.all([
      cadenceGqlCall(CADENCE_STATS_QUERY, { cadenceId: id }).catch(() => null),
      cadenceGqlCall(CADENCE_STEP_STATS_QUERY, {
        cadenceId: id,
        ...(options.stepStatsTimeRange ? { timeRange: options.stepStatsTimeRange } : {}),
      }).catch(() => null),
      cadenceGqlCall(CADENCE_GOAL_PROGRESS_QUERY, { cadenceId: id }).catch(() => null),
    ])
      .then(([statsData, stepStatsData, goalData]) => {
        if (!cancelled) {
          if (statsData) setStats(statsData.cadenceStats);
          if (stepStatsData) setStepStats(stepStatsData.cadenceStepStats || []);
          if (goalData) setGoalProgress(goalData.cadenceGoalProgress);
          if (!statsData && !stepStatsData && !goalData) {
            setError('Failed to fetch stats');
          } else {
            hasLoadedRef.current = true;
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cadenceId, fetchKey, options.stepStatsTimeRange]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { stats, stepStats, goalProgress, loading, error, refetch };
};

export default useCadenceStats;
